import { useEffect, useMemo, useState } from "react";
import BudgetModuleListCard from "../components/BudgetModuleListCard";
import BudgetModulePageShell from "../components/BudgetModulePageShell";
import { AppLoader, ButtonLoader } from "../components/Loaders";
import { getReferenceData, getWorkspace } from "../api/budgetPortal";
import { useAuth } from "../state/AuthContext";
import { useBudgetSocket } from "../state/BudgetSocketContext";
import { useBudgetModuleYears } from "../hooks/useBudgetModuleYears";
import { isAccountantUser } from "../utils/userRole";
import { downloadBudgetExcel, downloadBudgetPdf } from "../utils/budgetExports";
import {
  ADJUSTMENT_SCOPES,
  REPORT_HEADERS,
  DEPARTMENT_LABELS,
  applyEnabledDepartmentsToRows,
  buildActualOutcomeMap,
  buildEnabledDepartmentsMap,
  buildLineItemOptions,
  buildPlannerHeaderLabels,
  buildPlannerWorkspaceRows,
  buildPreviousBudgetMap,
  buildFallbackBudgetRowsFromActuals,
  formatAmount,
  getDepartmentCardsFromRows,
  getPlannerEmptyStateMessage,
  getPlannerRowsForDepartment,
  toAbsoluteInteger,
} from "../utils/budgetWorkspace";

const formatScopeToastMessage = (summary = {}) => {
  const financialYear = summary?.financial_year
    ? ` for ${summary.financial_year}`
    : "";
  const label = summary?.label || "Budget change";

  if (
    summary?.scope === ADJUSTMENT_SCOPES.singleLineItem &&
    summary?.line_item_title
  ) {
    return `${summary.line_item_title}: ${formatAmount(
      summary.previous_value,
    )} => ${formatAmount(summary.next_value)}${financialYear}`;
  }

  if (summary?.percentage !== null && summary?.percentage !== undefined) {
    return `${label} changed by ${summary.percentage}%${financialYear}`;
  }

  if (
    summary?.previous_value !== null &&
    summary?.previous_value !== undefined &&
    summary?.next_value !== null &&
    summary?.next_value !== undefined
  ) {
    return `${label}: ${formatAmount(summary.previous_value)} => ${formatAmount(
      summary.next_value,
    )}${financialYear}`;
  }

  return `${label}${financialYear}`;
};

const BudgetModulePlannerPage = () => {
  const { token, user } = useAuth();
  const isAccountant = isAccountantUser(user);
  const budgetSocket = useBudgetSocket();
  const { yearCards, selectedYear, setSelectedYear, loading, error } =
    useBudgetModuleYears(token);
  const [workspaceData, setWorkspaceData] = useState(null);
  const [allWorkspaceRows, setAllWorkspaceRows] = useState([]);
  const [workspaceRows, setWorkspaceRows] = useState([]);
  const [actualOutcomeMap, setActualOutcomeMap] = useState({});
  const [previousBudgetMap, setPreviousBudgetMap] = useState({});
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState(0);
  const [adjustmentScope, setAdjustmentScope] = useState(
    ADJUSTMENT_SCOPES.wholeBudget,
  );
  const [selectedAdjustmentDepartment, setSelectedAdjustmentDepartment] =
    useState("");
  const [selectedLineItem, setSelectedLineItem] = useState("");
  const [adjustmentPercentage, setAdjustmentPercentage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [estimateDrafts, setEstimateDrafts] = useState({});
  const [pendingOwnerRequest, setPendingOwnerRequest] = useState(null);
  const [ownerRemark, setOwnerRemark] = useState("");
  const [remarkError, setRemarkError] = useState("");
  const [saving, setSaving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [excelDownloading, setExcelDownloading] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const enabledDepartments = useMemo(
    () =>
      buildEnabledDepartmentsMap(
        workspaceData?.workspace?.department_details ?? null,
        allWorkspaceRows,
      ),
    [allWorkspaceRows, workspaceData?.workspace?.department_details],
  );

  const visibleDepartments = useMemo(() => {
    const enabledRows = applyEnabledDepartmentsToRows(
      allWorkspaceRows,
      enabledDepartments,
    );

    if (isAccountant) {
      const assignments =
        workspaceData?.workspace?.department_details?.assignments ?? {};

      return getDepartmentCardsFromRows(enabledRows).filter((item) =>
        Boolean(assignments[item.key]),
      );
    }

    const departments = getDepartmentCardsFromRows(enabledRows);
    return departments;
  }, [
    allWorkspaceRows,
    enabledDepartments,
    isAccountant,
    workspaceData?.workspace?.department_details,
  ]);

  const selectedDepartmentCard =
    visibleDepartments[selectedDepartment] ?? visibleDepartments[0];
  const isExecutiveSummary =
    selectedDepartmentCard?.key === REPORT_HEADERS.executiveSummary ||
    selectedDepartmentCard?.key === "executiveSummary";
  const plannerHeaderLabels = useMemo(
    () =>
      buildPlannerHeaderLabels(
        selectedYear,
        workspaceData?.previous_accounting_snapshot?.financial_year ?? "",
      ),
    [selectedYear, workspaceData?.previous_accounting_snapshot?.financial_year],
  );
  const selectableDepartments = useMemo(
    () =>
      getDepartmentCardsFromRows(allWorkspaceRows).filter(
        (item) => item.key !== "executiveSummary",
      ),
    [allWorkspaceRows],
  );

  const plannerRows = useMemo(() => {
    return getPlannerRowsForDepartment(
      workspaceRows,
      selectedDepartmentCard?.key ?? "executiveSummary",
      actualOutcomeMap,
      previousBudgetMap,
    );
  }, [
    actualOutcomeMap,
    previousBudgetMap,
    selectedDepartmentCard?.key,
    workspaceRows,
  ]);
  const allPlannerRows = useMemo(() => {
    return getPlannerRowsForDepartment(
      workspaceRows,
      "executiveSummary",
      actualOutcomeMap,
      previousBudgetMap,
    );
  }, [actualOutcomeMap, previousBudgetMap, workspaceRows]);
  const lineItemOptions = useMemo(
    () =>
      buildLineItemOptions(isExecutiveSummary ? allPlannerRows : plannerRows),
    [allPlannerRows, isExecutiveSummary, plannerRows],
  );
  const filteredPlannerRows = useMemo(() => {
    const normalizedSearch = `${searchTerm}`.trim().toLowerCase();

    if (!normalizedSearch) {
      return plannerRows;
    }

    return plannerRows.filter((row) => {
      const searchableValues = [
        row.title,
        formatAmount(row.originalBudget),
        formatAmount(row.actualOutcome),
        formatAmount(row.estimatedBudget),
        `${row.originalBudget ?? ""}`,
        `${row.actualOutcome ?? ""}`,
        `${row.estimatedBudget ?? ""}`,
      ]
        .map((value) => `${value ?? ""}`.toLowerCase())
        .filter(Boolean);

      return searchableValues.some((value) => value.includes(normalizedSearch));
    });
  }, [plannerRows, searchTerm]);
  const exportSections = useMemo(() => {
    return visibleDepartments.map((department) => ({
      key: department.key,
      name: department.name,
      rows: getPlannerRowsForDepartment(
        workspaceRows,
        department.key,
        actualOutcomeMap,
        previousBudgetMap,
      ),
    }));
  }, [actualOutcomeMap, previousBudgetMap, visibleDepartments, workspaceRows]);
  const hasExportData = useMemo(
    () => exportSections.some((section) => section.rows.length > 0),
    [exportSections],
  );

  useEffect(() => {
    if (!selectedYear) {
      setWorkspaceData(null);
      setAllWorkspaceRows([]);
      setWorkspaceRows([]);
      setActualOutcomeMap({});
      setPreviousBudgetMap({});
      setWorkspaceLoading(false);
      return;
    }

    const loadWorkspace = async () => {
      setWorkspaceLoading(true);
      setWorkspaceError("");

      try {
        const [workspaceResponse, referenceResponse] = await Promise.all([
          getWorkspace(token, selectedYear),
          getReferenceData(token),
        ]);
        const workspace = workspaceResponse?.data ?? null;
        const referenceRows = buildPlannerWorkspaceRows(
          workspace?.workspace?.budget_rows ?? [],
          referenceResponse?.data?.vicode_list ?? [],
        );
        const nextAllWorkspaceRows =
          referenceRows.length > 0
            ? referenceRows
            : buildFallbackBudgetRowsFromActuals(
                workspace?.previous_accounting_snapshot?.year_financials ?? [],
                referenceResponse?.data?.vicode_list ?? [],
              );
        const nextEnabledDepartments = buildEnabledDepartmentsMap(
          workspace?.workspace?.department_details ?? null,
          nextAllWorkspaceRows,
        );
        const nextWorkspaceRows = applyEnabledDepartmentsToRows(
          nextAllWorkspaceRows,
          nextEnabledDepartments,
        );
        setWorkspaceData(workspace);
        setAllWorkspaceRows(nextAllWorkspaceRows);
        setWorkspaceRows(nextWorkspaceRows);
        setPreviousBudgetMap(
          buildPreviousBudgetMap(
            workspace?.previous_workspace?.budget_rows ?? [],
          ),
        );
        setActualOutcomeMap(
          buildActualOutcomeMap(
            workspace?.previous_accounting_snapshot?.year_financials ?? [],
            workspace?.previous_workspace?.budget_rows ??
              workspace?.workspace?.budget_rows ??
              [],
          ),
        );
      } catch (err) {
        setWorkspaceError(
          err?.response?.data?.message ||
            err.message ||
            "Unable to load planner workspace",
        );
      }

      setWorkspaceLoading(false);
    };

    loadWorkspace();
  }, [selectedYear, token]);

  useEffect(() => {
    if (selectedDepartment >= visibleDepartments.length) {
      setSelectedDepartment(0);
    }
  }, [selectedDepartment, visibleDepartments.length]);

  useEffect(() => {
    setEstimateDrafts({});
    setSelectedLineItem("");
  }, [selectedDepartmentCard?.key, selectedYear, workspaceRows]);

  useEffect(() => {
    setSearchTerm("");
  }, [selectedYear]);

  useEffect(() => {
    if (isExecutiveSummary) {
      if (
        ![
          ADJUSTMENT_SCOPES.wholeBudget,
          ADJUSTMENT_SCOPES.department,
          ADJUSTMENT_SCOPES.singleLineItem,
        ].includes(adjustmentScope)
      ) {
        setAdjustmentScope(ADJUSTMENT_SCOPES.wholeBudget);
      }
      return;
    }

    if (
      ![
        ADJUSTMENT_SCOPES.wholeDepartment,
        ADJUSTMENT_SCOPES.singleLineItem,
      ].includes(adjustmentScope)
    ) {
      setAdjustmentScope(ADJUSTMENT_SCOPES.wholeDepartment);
    }
  }, [adjustmentScope, isExecutiveSummary]);

  useEffect(() => {
    if (!selectedAdjustmentDepartment && selectableDepartments.length > 0) {
      setSelectedAdjustmentDepartment(selectableDepartments[0].key);
      return;
    }

    if (
      selectedAdjustmentDepartment &&
      !selectableDepartments.some(
        (item) => item.key === selectedAdjustmentDepartment,
      )
    ) {
      setSelectedAdjustmentDepartment(selectableDepartments[0]?.key ?? "");
    }
  }, [selectableDepartments, selectedAdjustmentDepartment]);

  useEffect(() => {
    if (
      selectedLineItem &&
      !lineItemOptions.some((item) => item.value === selectedLineItem)
    ) {
      setSelectedLineItem("");
    }
  }, [lineItemOptions, selectedLineItem]);

  useEffect(() => {
    const update = budgetSocket?.workspaceUpdate;

    if (
      update?.received_on &&
      update.financial_year === selectedYear &&
      update.summary
    ) {
      setActionMessage(formatScopeToastMessage(update.summary));
    }
  }, [budgetSocket?.workspaceUpdate, selectedYear]);

  useEffect(() => {
    if (!actionMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setActionMessage("");
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [actionMessage]);

  const persistBudgetRows = async (nextRows, changeSummary = null) => {
    if (!budgetSocket?.connected) {
      setWorkspaceError(
        "Disconnected - internet required to save planner changes.",
      );
      return;
    }

    const workspace = workspaceData?.workspace ?? {};
    const nextVisibleRows = applyEnabledDepartmentsToRows(
      nextRows,
      buildEnabledDepartmentsMap(
        workspace.department_details ?? null,
        nextRows,
      ),
    );
    setSaving(true);
    setWorkspaceError("");
    setAllWorkspaceRows(nextRows);
    setWorkspaceRows(nextVisibleRows);
    setWorkspaceData((currentValue) => ({
      ...(currentValue ?? {}),
      workspace: {
        ...(currentValue?.workspace ?? {}),
        budget_rows: nextRows,
      },
    }));

    try {
      await budgetSocket.saveWorkspace({
        financial_year: selectedYear,
        budget_rows: nextRows,
        planner_details: workspace.planner_details ?? null,
        department_details: workspace.department_details ?? null,
        bootstrap_metadata: workspace.bootstrap_metadata ?? null,
        change_summary: changeSummary,
      });
    } catch (saveError) {
      setWorkspaceError(saveError.message || "Unable to save planner changes");
    }

    setSaving(false);
  };

  const getRowsForScope = (scope) => {
    if (scope === ADJUSTMENT_SCOPES.wholeBudget) {
      return workspaceRows;
    }

    if (scope === ADJUSTMENT_SCOPES.department) {
      return workspaceRows.filter((row) =>
        Array.isArray(row.departments)
          ? row.departments.includes(selectedAdjustmentDepartment)
          : false,
      );
    }

    if (scope === ADJUSTMENT_SCOPES.wholeDepartment) {
      return plannerRows;
    }

    if (scope === ADJUSTMENT_SCOPES.singleLineItem) {
      return workspaceRows.filter(
        (row) => `${row.viCode}` === `${selectedLineItem}`,
      );
    }

    return [];
  };

  const getTargetDepartments = (scope, metadata = {}) => {
    if (
      Array.isArray(metadata.target_departments) &&
      metadata.target_departments.length > 0
    ) {
      return metadata.target_departments;
    }

    if (scope === ADJUSTMENT_SCOPES.wholeBudget) {
      return selectableDepartments.map((item) => item.key);
    }

    if (scope === ADJUSTMENT_SCOPES.department) {
      return selectedAdjustmentDepartment ? [selectedAdjustmentDepartment] : [];
    }

    if (scope === ADJUSTMENT_SCOPES.wholeDepartment) {
      return selectedDepartmentCard?.key &&
        DEPARTMENT_LABELS[selectedDepartmentCard.key]
        ? [selectedDepartmentCard.key]
        : [];
    }

    if (scope === ADJUSTMENT_SCOPES.singleLineItem) {
      const lineItemRow = workspaceRows.find(
        (row) => `${row.viCode}` === `${selectedLineItem}`,
      );

      if (lineItemRow?.departments?.length) {
        return lineItemRow.departments;
      }

      return selectedDepartmentCard?.key &&
        DEPARTMENT_LABELS[selectedDepartmentCard.key]
        ? [selectedDepartmentCard.key]
        : [];
    }

    return [];
  };

  const getChangedRows = (nextRows, targetCodes) => {
    return nextRows
      .filter((row) => targetCodes.has(`${row.viCode}`))
      .map((row) => ({
        viCode: row.viCode,
        viDescription: row.viDescription,
        codeDescription: row.codeDescription,
        estimatedCurrentYear: row.estimatedCurrentYear,
        estimatedPriorYear: row.estimatedPriorYear,
        actualPriorYear: row.actualPriorYear,
        departments: Array.isArray(row.departments) ? row.departments : [],
        category: row.category,
        sourceLineItems: Array.isArray(row.sourceLineItems)
          ? row.sourceLineItems
          : [],
      }));
  };

  const requestOwnerChange = async ({
    requestType,
    scope,
    nextRows,
    targetCodes,
    metadata = {},
  }) => {
    const confirmed = window.confirm(
      "This change will be sent to Change Request for approval.",
    );

    if (!confirmed) {
      return;
    }

    if (!budgetSocket?.connected) {
      setWorkspaceError(
        "Disconnected - internet required to send change request.",
      );
      return;
    }

    const targetDepartments = getTargetDepartments(scope, metadata);
    const proposedRows = getChangedRows(nextRows, targetCodes);

    if (targetDepartments.length === 0 || proposedRows.length === 0) {
      setWorkspaceError(
        "Unable to determine target departments for this request.",
      );
      return;
    }

    setSaving(true);
    setWorkspaceError("");
    setActionMessage("");
    setRemarkError("");
    setOwnerRemark("");
    setPendingOwnerRequest({
      financial_year: selectedYear,
      request_type: requestType,
      scope,
      target_departments: targetDepartments,
      proposed_rows: proposedRows,
      metadata: {
        ...metadata,
        financial_year: selectedYear,
        scope,
        target_departments: targetDepartments,
      },
    });
    setSaving(false);
  };

  const submitOwnerChangeRequest = async (event) => {
    event.preventDefault();

    const remark = ownerRemark.trim();
    if (remark.length < 3 || remark.length > 500) {
      setRemarkError("Enter a remark between 3 and 500 characters.");
      return;
    }

    setSaving(true);
    setRemarkError("");
    setWorkspaceError("");
    setActionMessage("");

    try {
      await budgetSocket.createChangeRequest({
        ...pendingOwnerRequest,
        remark,
      });
      setActionMessage("Change sent for approval.");
      setPendingOwnerRequest(null);
      setOwnerRemark("");
    } catch (requestError) {
      setWorkspaceError(
        requestError.message || "Unable to send change request.",
      );
    }

    setSaving(false);
  };

  const handleEstimatedBudgetSave = async (viCode, value) => {
    const normalizedValue = toAbsoluteInteger(value);
    const currentRow = allWorkspaceRows.find(
      (row) => `${row.viCode}` === `${viCode}`,
    );
    const plannerRow = allPlannerRows.find(
      (row) => `${row.viCode}` === `${viCode}`,
    );

    if (
      currentRow &&
      toAbsoluteInteger(currentRow.estimatedCurrentYear) === normalizedValue
    ) {
      setEstimateDrafts((currentValue) => {
        const nextValue = { ...currentValue };
        delete nextValue[`${viCode}`];
        return nextValue;
      });
      return;
    }

    const targetCodes = new Set([`${viCode}`]);
    const nextRows = allWorkspaceRows.map((row) =>
      `${row.viCode}` === `${viCode}`
        ? { ...row, estimatedCurrentYear: normalizedValue }
        : row,
    );

    if (!isAccountant) {
      const targetDepartments =
        Array.isArray(currentRow?.departments) &&
        currentRow.departments.length > 0
          ? currentRow.departments
          : selectedDepartmentCard?.key &&
              DEPARTMENT_LABELS[selectedDepartmentCard.key]
            ? [selectedDepartmentCard.key]
            : [];
      await requestOwnerChange({
        requestType: "manual",
        scope: ADJUSTMENT_SCOPES.singleLineItem,
        nextRows,
        targetCodes,
        metadata: {
          vi_code: `${viCode}`,
          line_item_title:
            plannerRow?.title ||
            currentRow?.viDescription ||
            currentRow?.codeDescription ||
            `${viCode}`,
          previous_value: toAbsoluteInteger(currentRow?.estimatedCurrentYear),
          next_value: normalizedValue,
          department_key:
            !isExecutiveSummary && selectedDepartmentCard?.key
              ? selectedDepartmentCard.key
              : "",
          target_departments: targetDepartments,
        },
      });
      setEstimateDrafts((currentValue) => {
        const nextValue = { ...currentValue };
        delete nextValue[`${viCode}`];
        return nextValue;
      });
      return;
    }

    await persistBudgetRows(nextRows, {
      scope: ADJUSTMENT_SCOPES.singleLineItem,
      financial_year: selectedYear,
      line_item_title:
        plannerRow?.title ||
        currentRow?.viDescription ||
        currentRow?.codeDescription ||
        `${viCode}`,
      previous_value: toAbsoluteInteger(currentRow?.estimatedCurrentYear),
      next_value: normalizedValue,
      vi_code: `${viCode}`,
      department_key: selectedDepartmentCard?.key ?? "",
      label: "Single line item",
    });
    setEstimateDrafts((currentValue) => {
      const nextValue = { ...currentValue };
      delete nextValue[`${viCode}`];
      return nextValue;
    });
  };

  const handleApplyAdjustment = async () => {
    setWorkspaceError("");
    const parsedPercentage = Number.parseFloat(adjustmentPercentage);
    if (Number.isNaN(parsedPercentage)) {
      setWorkspaceError("Enter a valid percentage to apply.");
      return;
    }

    const boundedPercentage = Math.max(-50, Math.min(50, parsedPercentage));
    const factor = 1 + boundedPercentage / 100;
    const targetRows = getRowsForScope(adjustmentScope);

    if (
      adjustmentScope === ADJUSTMENT_SCOPES.department &&
      !selectedAdjustmentDepartment
    ) {
      setWorkspaceError("Select a department to continue.");
      return;
    }

    if (
      adjustmentScope === ADJUSTMENT_SCOPES.singleLineItem &&
      !selectedLineItem
    ) {
      setWorkspaceError("Select a line item to continue.");
      return;
    }

    const targetCodes = new Set(targetRows.map((row) => `${row.viCode}`));

    if (targetCodes.size === 0) {
      setWorkspaceError("No line items available for the selected scope.");
      return;
    }

    const nextRows = allWorkspaceRows.map((row) => {
      if (!targetCodes.has(`${row.viCode}`)) {
        return row;
      }

      return {
        ...row,
        estimatedCurrentYear: Math.abs(
          Math.round((Number(row.estimatedCurrentYear) || 0) * factor),
        ),
      };
    });
    const targetDepartments = getTargetDepartments(adjustmentScope);
    const selectedLineItemRow = lineItemOptions.find(
      (item) => item.value === selectedLineItem,
    );
    const summaryMetadata = {
      percentage: boundedPercentage,
      affected_line_item_count: targetCodes.size,
      department_key:
        adjustmentScope === ADJUSTMENT_SCOPES.department
          ? selectedAdjustmentDepartment
          : adjustmentScope === ADJUSTMENT_SCOPES.wholeDepartment
            ? (selectedDepartmentCard?.key ?? "")
            : "",
      target_departments: targetDepartments,
      vi_code:
        adjustmentScope === ADJUSTMENT_SCOPES.singleLineItem
          ? selectedLineItem
          : "",
      line_item_title:
        adjustmentScope === ADJUSTMENT_SCOPES.singleLineItem
          ? (selectedLineItemRow?.title ?? "")
          : "",
    };

    if (!isAccountant) {
      await requestOwnerChange({
        requestType: "adjustment",
        scope: adjustmentScope,
        nextRows,
        targetCodes,
        metadata: summaryMetadata,
      });
      return;
    }

    await persistBudgetRows(nextRows, {
      scope: adjustmentScope,
      financial_year: selectedYear,
      label:
        adjustmentScope === ADJUSTMENT_SCOPES.wholeBudget
          ? "Whole budget"
          : adjustmentScope === ADJUSTMENT_SCOPES.department
            ? (DEPARTMENT_LABELS[selectedAdjustmentDepartment] ?? "Department")
            : adjustmentScope === ADJUSTMENT_SCOPES.wholeDepartment
              ? `Whole ${selectedDepartmentCard?.name ?? "department"}`
              : "Single line item",
      ...summaryMetadata,
    });
  };

  const handleDownloadPdf = async () => {
    if (!hasExportData) {
      setActionMessage("No budget data is available to export for this year.");
      return;
    }

    setPdfDownloading(true);
    setWorkspaceError("");

    try {
      downloadBudgetPdf({
        financialYear: selectedYear,
        plannerHeaderLabels,
        sections: exportSections,
      });
      setActionMessage("PDF download started.");
    } catch (downloadError) {
      setWorkspaceError(
        downloadError.message || "Unable to download the planner PDF.",
      );
    }

    setPdfDownloading(false);
  };

  const handleDownloadExcel = async () => {
    if (!hasExportData) {
      setActionMessage("No budget data is available to export for this year.");
      return;
    }

    setExcelDownloading(true);
    setWorkspaceError("");

    try {
      downloadBudgetExcel({
        financialYear: selectedYear,
        plannerHeaderLabels,
        sections: exportSections,
      });
      setActionMessage("Excel download started.");
    } catch (downloadError) {
      setWorkspaceError(
        downloadError.message || "Unable to download the planner Excel file.",
      );
    }

    setExcelDownloading(false);
  };

  return (
    <BudgetModulePageShell
      title="Budget Module Planner"
      description=""
      yearCards={yearCards}
      selectedYear={selectedYear}
      onSelectYear={setSelectedYear}
    >
      {error ? <div className="alert alert--error">{error}</div> : null}
      {workspaceError ? (
        <div className="alert alert--error">{workspaceError}</div>
      ) : null}
      {actionMessage ? (
        <div className="budget-toast">{actionMessage}</div>
      ) : null}

      {loading || workspaceLoading ? (
        <AppLoader label="Loading planner..." />
      ) : !selectedYear ? (
        <p>
          No budget years are available yet. Start from Bootstrap and Mapping.
        </p>
      ) : (
        <div className="bp-base">
          {!workspaceRows.length ? (
            <div className="bp-emptyState">
              <p style={{ margin: 0 }}>
                {getPlannerEmptyStateMessage(
                  Boolean(workspaceData?.accounting_snapshot),
                )}
              </p>
            </div>
          ) : (
            <div className="bp-layoutContainer">
              <div className="bp-listContainer">
                {visibleDepartments.map((item, index) => (
                  <BudgetModuleListCard
                    key={item.key}
                    title={item.name}
                    isSelected={selectedDepartment === index}
                    onClick={() => setSelectedDepartment(index)}
                  />
                ))}
              </div>

              <div className="bp-container">
                <div className="bp-controls">
                  <div className="bp-exportActions">
                    <button
                      type="button"
                      className="button-secondary"
                      disabled={pdfDownloading || !hasExportData}
                      onClick={handleDownloadPdf}
                    >
                      {pdfDownloading ? (
                        <ButtonLoader label="Preparing PDF..." />
                      ) : (
                        "PDF"
                      )}
                    </button>
                    <button
                      type="button"
                      className="button-secondary"
                      disabled={excelDownloading || !hasExportData}
                      onClick={handleDownloadExcel}
                    >
                      {excelDownloading ? (
                        <ButtonLoader label="Preparing Excel..." />
                      ) : (
                        "Excel"
                      )}
                    </button>
                  </div>
                  <div className="bp-controlsGroup">
                    <div className="bp-controlField">
                      <label className="bp-controlLabel">Search</label>
                      <input
                        className="bp-controlInput bp-searchInput"
                        type="search"
                        placeholder="Search line item or amount"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                      />
                    </div>
                    <div className="bp-controlField">
                      <label className="bp-controlLabel">
                        Adjustment Scope
                      </label>
                      <select
                        className="bp-controlSelect"
                        value={adjustmentScope}
                        disabled={!budgetSocket?.connected || saving}
                        onChange={(event) =>
                          setAdjustmentScope(event.target.value)
                        }
                      >
                        {isExecutiveSummary ? (
                          <>
                            <option value={ADJUSTMENT_SCOPES.wholeBudget}>
                              Whole budget
                            </option>
                            <option value={ADJUSTMENT_SCOPES.department}>
                              Department
                            </option>
                            <option value={ADJUSTMENT_SCOPES.singleLineItem}>
                              Single line item
                            </option>
                          </>
                        ) : (
                          <>
                            <option value={ADJUSTMENT_SCOPES.wholeDepartment}>
                              Whole department
                            </option>
                            <option value={ADJUSTMENT_SCOPES.singleLineItem}>
                              Single line item
                            </option>
                          </>
                        )}
                      </select>
                    </div>
                    {adjustmentScope === ADJUSTMENT_SCOPES.department ? (
                      <div className="bp-controlField">
                        <label className="bp-controlLabel">Department</label>
                        <select
                          className="bp-controlSelect"
                          value={selectedAdjustmentDepartment}
                          disabled={!budgetSocket?.connected || saving}
                          onChange={(event) =>
                            setSelectedAdjustmentDepartment(event.target.value)
                          }
                        >
                          {selectableDepartments.map((item) => (
                            <option key={item.key} value={item.key}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                    {adjustmentScope === ADJUSTMENT_SCOPES.singleLineItem ? (
                      <div className="bp-controlField">
                        <label className="bp-controlLabel">Line item</label>
                        <select
                          className="bp-controlSelect"
                          value={selectedLineItem}
                          disabled={!budgetSocket?.connected || saving}
                          onChange={(event) =>
                            setSelectedLineItem(event.target.value)
                          }
                        >
                          <option value="">Select line item</option>
                          {lineItemOptions.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                    <div className="bp-controlField">
                      <label className="bp-controlLabel">Percentage</label>
                      <input
                        className="bp-controlInput"
                        type="number"
                        placeholder="e.g. 10 or -5"
                        value={adjustmentPercentage}
                        min={-50}
                        max={50}
                        step="0.01"
                        disabled={!budgetSocket?.connected || saving}
                        onChange={(event) =>
                          setAdjustmentPercentage(event.target.value)
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            handleApplyAdjustment();
                          }
                        }}
                      />
                    </div>
                    <div className="bp-controlActions">
                      <button
                        type="button"
                        disabled={
                          !budgetSocket?.connected ||
                          saving ||
                          plannerRows.length === 0
                        }
                        onClick={handleApplyAdjustment}
                      >
                        {saving ? (
                          <ButtonLoader label="Saving..." />
                        ) : (
                          "Apply Adjustment"
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="all-table-wrapper">
                  <div className="all-table-container">
                    <div className="all-table">
                      <div className="all-table-row all-table-header">
                        <div className="all-table-cell">
                          {selectedDepartmentCard?.name ??
                            REPORT_HEADERS.executiveSummary}
                        </div>
                        <div
                          className="all-table-cell"
                          style={{ textAlign: "right" }}
                        >
                          <span className="bp-headerTitle">
                            {plannerHeaderLabels.previousBudget.title}
                          </span>
                          <span className="bp-headerYear">
                            {plannerHeaderLabels.previousBudget.year}
                          </span>
                        </div>
                        <div
                          className="all-table-cell"
                          style={{ textAlign: "right" }}
                        >
                          <span className="bp-headerTitle">
                            {plannerHeaderLabels.actualOutcome.title}
                          </span>
                          <span className="bp-headerYear">
                            {plannerHeaderLabels.actualOutcome.year}
                          </span>
                        </div>
                        <div
                          className="all-table-cell"
                          style={{ textAlign: "right" }}
                        >
                          <span className="bp-headerTitle">
                            {plannerHeaderLabels.estimated.title}
                          </span>
                          <span className="bp-headerYear">
                            {plannerHeaderLabels.estimated.year}
                          </span>
                        </div>
                      </div>

                      {plannerRows.length === 0 ? (
                        <div className="bp-emptyTableState">
                          No budget rows found for this department.
                        </div>
                      ) : filteredPlannerRows.length === 0 ? (
                        <div className="bp-emptyTableState">
                          No line items match your search.
                        </div>
                      ) : (
                        filteredPlannerRows.map((row) => (
                          <div
                            className="all-table-row"
                            key={`${row.viCode}-${row.title}`}
                          >
                            <div className="all-table-cell">{row.title}</div>
                            <div
                              className="all-table-cell"
                              style={{ textAlign: "right" }}
                            >
                              {formatAmount(row.originalBudget)}
                            </div>
                            <div
                              className="all-table-cell"
                              style={{ textAlign: "right" }}
                            >
                              {formatAmount(row.actualOutcome)}
                            </div>
                            <div
                              className="all-table-cell"
                              style={{ textAlign: "right" }}
                            >
                              <input
                                className="bp-cell-input"
                                type="number"
                                value={
                                  estimateDrafts[`${row.viCode}`] ??
                                  row.estimatedBudget
                                }
                                disabled={!budgetSocket?.connected || saving}
                                onChange={(event) =>
                                  setEstimateDrafts((currentValue) => ({
                                    ...currentValue,
                                    [`${row.viCode}`]: event.target.value,
                                  }))
                                }
                                onBlur={() =>
                                  handleEstimatedBudgetSave(
                                    row.viCode,
                                    estimateDrafts[`${row.viCode}`] ??
                                      row.estimatedBudget,
                                  )
                                }
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.currentTarget.blur();
                                  }
                                }}
                              />
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {pendingOwnerRequest ? (
        <div className="budget-modalOverlay">
          <form
            className="budget-remarkModal"
            onSubmit={submitOwnerChangeRequest}
          >
            <h3>Change request remark</h3>
            <p>This change will be sent to Change Request for approval.</p>
            <textarea
              value={ownerRemark}
              maxLength={500}
              placeholder="Write why this change request is being made"
              onChange={(event) => setOwnerRemark(event.target.value)}
              autoFocus
            />
            {remarkError ? (
              <div className="web-inlineError">{remarkError}</div>
            ) : null}
            <div className="budget-remarkModal__actions">
              <button
                type="button"
                className="button-secondary"
                onClick={() => {
                  setPendingOwnerRequest(null);
                  setOwnerRemark("");
                  setRemarkError("");
                }}
                disabled={saving}
              >
                Cancel
              </button>
              <button type="submit" disabled={saving}>
                {saving ? (
                  <ButtonLoader label="Sending..." />
                ) : (
                  "Send for approval"
                )}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </BudgetModulePageShell>
  );
};

export default BudgetModulePlannerPage;
